import {colorSwatch} from '../utils/colorSwatch.js';
import {MenuPanels} from '../../models/UIModel.js';
import React, {useState, useEffect} from 'react';
import {MenuButton} from '../components/menu-button.js';

import {
    Column,
    Container,
    Image,
    Panel,
    PanelProps,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {Align, FlexDirection, Justify, PositionType} from '@wonderlandengine/react-ui';
import {goToMenu} from '../utils/goToMenu.js';
import {
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {Window} from '../components/window.js';
import {Statistics} from '../../components/statistics.js';
import {TransactionalNames} from '../../enums/TransactionalNames.js';
import {WindowHeader} from '../components/window-header.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {StoreInventoryItems} from '../../enums/StoreInventoryItems.js';
import {StorePowerupsData} from '../../components/store-data/powerups/StorePowerupsData.js';
import {Texture} from '@wonderlandengine/api';
import {Localization} from '../../utils/Localization.js';
import {resizeLabelToFit} from '../utils/utils.js';
import {Sounds} from '../../enums/Sounds.js';
import {goToPreviousMenu} from '../utils/goToPreviousMenu.js';

export function isStoreItemLocked(storeItem: StoreInventoryItems) {
    const data = StorePowerupsData.instance.data.get(storeItem);
    return Statistics.instance.getTransactional(data.stat) == 0;
}

export function lockImage(storeItem: StoreInventoryItems, image: Texture): Texture {
    if (!isStoreItemLocked(storeItem)) {
        return image;
    } else return UiTexturesManager.instance.buttonLock;
}

export const ShoppeBase = (
    props: React.PropsWithChildren<PanelProps> & {
        onOkay: (e: {x: number; y: number; e: MouseEvent}) => void;
        buttonText?: string;
        title: string;
        hideBuyCoins?: boolean;
        showBackButton?: boolean;
        currentMenu?: MenuPanels;
        animateButton?: boolean;
    }
) => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;

    const [currentCoins, setCurrentCoins] = useState(
        Statistics.instance.getTransactional(TransactionalNames.CoinCount)
    );

    useEffect(() => {
        const listener = () => {
            setCurrentCoins(
                Statistics.instance.getTransactional(TransactionalNames.CoinCount)
            );
        };

        Statistics.instance.registerChangedCallback(listener);
        return () => {
            Statistics.instance.unRegisterChangedCallback(listener);
        };
    }, [currentCoins]);

    return (
        <Container
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Row}
            justifyContent={Justify.Center}
        >
            <Column marginTop={36}>
                <Window>
                    <Column
                        height="100%"
                        marginTop={20}
                        alignSelf={Align.Center}
                        width={500}
                        rowGap={20}
                    >
                        <WindowHeader title={props.title} />
                        <Panel
                            flexGrow={0}
                            rounding={0}
                            material={rotbMats.hudWindowMaterial}
                            height={50}
                        >
                            <Row margin={5} width="100%">
                                <Image
                                    flexGrow={0}
                                    rounding={0}
                                    width={40}
                                    height={40}
                                    src={UiTexturesManager.instance.coinIconSmall}
                                ></Image>

                                <Container marginLeft={8} top={10} flexGrow={1}>
                                    <Text fontSize={24} color={colorSwatch.DescriptionText}>
                                        {currentCoins}
                                    </Text>
                                </Container>
                                {!props.hideBuyCoins && (
                                    <Container
                                        marginTop={-20}
                                        flexGrow={0}
                                        alignSelf={Align.FlexEnd}
                                        justifyContent={Justify.FlexEnd}
                                    >
                                        <MenuButton
                                            animate={true}
                                            width={200}
                                            height={75}
                                            color={colorSwatch.GetCoinsButton}
                                            fontSize={35}
                                            onClick={() => {
                                                goToMenu(MenuPanels.StoreCoins);
                                            }}
                                            text={Localization.instance.get('GetCoins')}
                                        ></MenuButton>
                                    </Container>
                                )}
                            </Row>
                        </Panel>
                        <Container flexGrow={1}>{props.children}</Container>
                    </Column>
                </Window>
                <Row justifyContent={Justify.Center}>
                    {props.showBackButton && (
                        <MenuButton
                            width={250}
                            height={54}
                            margin={6}
                            color={colorSwatch.CancelButton}
                            fontSize={32}
                            sound={Sounds.cancel}
                            onClick={() => {
                                goToPreviousMenu(props.currentMenu ?? MenuPanels.MainMenu);
                            }}
                        >
                            {Localization.instance.get('Back')}
                        </MenuButton>
                    )}
                    <MenuButton
                        width={250}
                        height={54}
                        margin={6}
                        color={colorSwatch.AcceptButton}
                        fontSize={32}
                        onClick={(e) => props.onOkay(e)}
                        animate={props.animateButton}
                    >
                        {props.buttonText ??
                            Localization.instance.get('PurchaseFailedDialogAccept')}
                    </MenuButton>
                </Row>
            </Column>
        </Container>
    );
};
